/* eslint-disable react/jsx-no-useless-fragment */
import * as React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { Avatar, Badge, Button, Typography, Alert } from '@mui/material';

import NoUserImage from '@kamernet/assets/no-user-image.webp';
import { OwnerTypeID } from '@kamernet/core/ApiClient/Kamernet';
import { AuthContext } from '@kamernet/core/Auth/AuthContext';
import { AuthSource, AuthAction } from '@kamernet/core/Auth/types';
import { useAuthenticationRouting } from '@kamernet/core/Auth/useAuthenticationRouting';
import { useIntl } from '@kamernet/core/Intl';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import { ROUTES } from '@kamernet/core/Routing/routes';
import { CommonStyles } from '@kamernet/styles/CommonStyles';
import { getFormattedDate } from '@kamernet/utilities/DateTime/getFormattedDate';
import { useMediaIsDesktop } from '@kamernet/utilities/Media/useMediaIsDesktop';
import { getImageUrl } from '@kamernet/utilities/Resources/getImageUrl';

import { OwnertypeTranslations } from '@kamernet/modules/Listing/translations';

import { ListingDetailsContext } from '../../ListingDetailsContext';
import { ContactCard } from '../ContactLandlord/components/ContactCard/ContactCard';
import { ReportListing } from '../ReportListing/ReportListing';

import styles from './LandlordInfo.module.css';

export const LandlordInfo = () => {
  const { listingDetails } = ListingDetailsContext.useContext();
  const { navigateToAuthentication } = useAuthenticationRouting();
  const { T, locale } = useIntl();
  const { isAuthenticated, userProfile } = AuthContext.useContext();
  const [openReport, setOpenReport] = React.useState<boolean>(false);

  const landlordLastLoggedIn = listingDetails.landlordLastLoggedOn
    ? getFormattedDate(listingDetails.landlordLastLoggedOn).toLocaleLowerCase()
    : '--';

  const landlordActiveSince = listingDetails.landlordMemberSince
    ? getFormattedDate(listingDetails.landlordMemberSince).toLocaleLowerCase()
    : '--';

  const isDesktop = useMediaIsDesktop();
  const ownerType = listingDetails.landlordTypeId || OwnerTypeID.Other;

  return (
    <section className={styles.root} id="about-landlord">
      <Typography className={CommonStyles.margin_bottom_2} variant="h5">
        {T('ldp.about_the_landlord')}
      </Typography>

      <React.Fragment>
        {listingDetails.isActive || listingDetails.isPreviewMode ? (
          <React.Fragment>
            {isAuthenticated ? (
              <div className={styles.content}>
                <div className={styles.header}>
                  <Badge
                    className={styles.badge}
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <React.Fragment>
                        {listingDetails.displayVerifiedBadge && (
                          <CheckCircleIcon
                            fontSize="medium"
                            color="primary"
                            className={styles.checkIcon}
                          />
                        )}
                      </React.Fragment>
                    }
                  >
                    <Avatar
                      alt={listingDetails.landlordDisplayName}
                      className={styles.badge}
                      src={
                        getImageUrl(listingDetails.landlordPhotoKey) ||
                        NoUserImage.src
                      }
                    />
                  </Badge>
                  <div>
                    <Typography variant="subtitle1">
                      {listingDetails.landlordDisplayName}
                    </Typography>
                    <div className={styles.status}>
                      {listingDetails.displayVerifiedBadge && (
                        <React.Fragment>
                          <Typography color="secondary.accent" variant="body2">
                            {T('Verified')}
                          </Typography>
                          <Typography variant="body3">•</Typography>
                        </React.Fragment>
                      )}

                      <Typography color="secondary.accent" variant="body2">
                        {T(OwnertypeTranslations[ownerType])}
                      </Typography>
                    </div>
                    <Typography color="secondary.accent" variant="body2">
                      {T('lbl.last_seen_%', landlordLastLoggedIn)}
                    </Typography>
                  </div>
                </div>
                <div className={styles.gridContainer}>
                  {listingDetails.responseRateText !== '-1%' &&
                    listingDetails.responseRateText !== '0%' && (
                      <div className={styles.gridItem}>
                        <Typography variant="body2">
                          {T('LBL_RESPONSE_RATE')}
                        </Typography>
                        <Typography variant="subtitle2">
                          {listingDetails.responseRateText}
                        </Typography>
                      </div>
                    )}

                  {listingDetails.responseTime && (
                    <div className={styles.gridItem}>
                      <Typography variant="body2">
                        {T('LBL_RESPONSETIME')}
                      </Typography>
                      <Typography variant="subtitle2">
                        {listingDetails.responseTime}
                      </Typography>
                    </div>
                  )}
                  {listingDetails.activeListingsCount !== null && (
                    <div className={styles.gridItem}>
                      <Typography variant="body2">
                        {T('lbl.rented_before')}
                      </Typography>
                      <Typography variant="subtitle2">
                        {listingDetails.activeListingsCount === 0
                          ? T('LBL_NEW_LANDLORD_TXT')
                          : T(
                              'ldp.about_landlord.rented_x_times',
                              listingDetails.activeListingsCount,
                            )}
                      </Typography>
                    </div>
                  )}

                  <div className={styles.gridItem}>
                    <Typography variant="body2">
                      {T('LBL_USER_PROFILE_DATE_REGISTRATION')}
                    </Typography>
                    <Typography variant="subtitle2">
                      {landlordActiveSince}
                    </Typography>
                  </div>
                </div>
                <div className={styles.buttons}>
                  <NavLink.Native
                    target="_blank"
                    href={ROUTES.LandlordPublicProfile.getURL(
                      {
                        landlordID: listingDetails.landlordUserId || 0, // @todo: investigate why listingDetails.landlordUserId is nullable
                        refid: listingDetails.listingId,
                      },
                      locale,
                      'prefix',
                    )}
                  >
                    <Button variant="outlined">{T('LBL_VIEW_PROFILE')}</Button>
                  </NavLink.Native>
                  <ContactCard variant="secondary" size="medium" />
                </div>
              </div>
            ) : (
              <div className={styles.content}>
                <div className={styles.headerAnonoymous}>
                  <Badge
                    className={styles.badge}
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <React.Fragment>
                        {listingDetails.displayVerifiedBadge && (
                          <CheckCircleIcon
                            fontSize="medium"
                            color="primary"
                            className={styles.checkIcon}
                          />
                        )}
                      </React.Fragment>
                    }
                  >
                    <Avatar
                      alt={listingDetails.landlordDisplayName}
                      className={styles.badge}
                      src={
                        getImageUrl(listingDetails.landlordPhotoKey) ||
                        NoUserImage.src
                      }
                    />
                  </Badge>
                  <div>
                    <Typography variant="subtitle1">
                      {listingDetails.landlordDisplayName}
                    </Typography>
                  </div>
                </div>
                <Typography variant="body2">
                  {T('LBL_NOT_LOGGED_IN_LANDLORD_DETAILS')}
                </Typography>
                <div className={styles.buttons}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      navigateToAuthentication({
                        action: AuthAction.Signup,
                        source: AuthSource.ClickedNavbar,
                      })
                    }
                  >
                    {T('LBL_BUTTON_REGISTER')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      navigateToAuthentication({
                        action: AuthAction.Login,
                        source: AuthSource.ClickedNavbar,
                      })
                    }
                  >
                    {T('LBL_LOGIN')}
                  </Button>
                </div>
              </div>
            )}
          </React.Fragment>
        ) : (
          <Alert severity="error">{T('LBL_RA_POSSIBLE_ABUSE')}</Alert>
        )}
      </React.Fragment>

      {!isDesktop && isAuthenticated && userProfile?.haspremiumaccount && (
        <Button
          className={styles.report}
          startIcon={<FlagOutlinedIcon className={styles.faintText} />}
          onClick={() => setOpenReport(true)}
        >
          {T('lbl.report_advert')}
        </Button>
      )}
      <ReportListing open={openReport} setOpen={setOpenReport} />
    </section>
  );
};
